import { useEffect, useState, useRef, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./Card";
import { Search, Filter, Phone, Map, ChevronDown } from "lucide-react";
import {
	Table,
	TableBody,
	TableCell,
	TableHeader,
	TableHead,
	TableRow,
} from "./Table";
import CustomerApi from "../../../../API/CustomerApi";
import ContactButtons from "../../../../Services/ContactButtons";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import AccountIssuesSkeleton, {
	AccountIssueRowSkeleton,
	AccountIssueMobileItemSkeleton,
} from "./skeletons/AccountIssuesSkeleton";
import clsx from "clsx";
import { getStatusColor } from "../constants/AccountStatuses";
import anonUser from "../../../../assets/images/userGrey.png";
import { Pagination, PaginationItem } from "@mui/material";
import { BiLeftArrow, BiRightArrow } from "react-icons/bi";
import CustomerInfo from "../../../../components/CustomerInfo";
import { MdOutlineMail, MdOutlinePhone } from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa6";

export default function AccountIssues({ fiberCompanyId }) {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [init, setInit] = useState(true);
	const [searchInput, setSearchInput] = useState("");

	const [showAccountStatusDropdown, setShowAccountStatusDropdown] =
		useState(false);
	const [showIssueTypeDropdown, setShowIssueTypeDropdown] = useState(false);
	const dropdownRef = useRef(null);
	const [totalPages, setTotalPages] = useState(0);
	const navigate = useNavigate();
	const [selectedCustomerForInfo, setSelectedCustomerForInfo] = useState(null);

	const [
		{ search: debouncedSearch, accountStatus, issueType, page },
		setFilters,
	] = useState({
		search: "",
		accountStatus: "",
		issueType: "",
		page: 1,
	});

	const setFiltersWrapper = (newFilters) => {
		setFilters((prevFilters) => ({
			...prevFilters,
			...(typeof newFilters === "function"
				? newFilters(prevFilters)
				: newFilters),
			page: 1,
		}));
	};

	const accountStatuses = [
		"", // For "All"
		"Scheduled",
		"Error",
		"Canceled",
		"Sold",
		"Prospect",
		"Rescheduled",
		"Completed",
		"Incomplete",
		"Clawed Back",
	];

	const issueTypes = [
		"", // For "All"
		"Cancel",
		"Reschedule",
	];

	useEffect(() => {
		setLoading(true);
		const timerId = setTimeout(() => {
			setFiltersWrapper((prev) => ({ ...prev, search: searchInput }));
		}, 750);

		return () => {
			clearTimeout(timerId);
			setLoading(false);
		};
	}, [searchInput]);

	// Handle click outside dropdowns
	useEffect(() => {
		function handleClickOutside(event) {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setShowAccountStatusDropdown(false);
				setShowIssueTypeDropdown(false);
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const res = await CustomerApi.SearchFidiumCustomers({
					searchString: debouncedSearch || undefined,
					accountStatus: accountStatus || undefined,
					issueType: issueType || undefined,
					hasIssue: true,
					fiberCompanyIds: fiberCompanyId ? [fiberCompanyId] : undefined,
					sortBy: "Issues",
					pagingParams: {
						pageNumber: page,
						pageSize: 10,
					},
				});
				const result = res.data.list.map((c) => ({
					...c,
					id: c.id,
					name: `${c.firstName} ${c.lastName}`.trim(),
					timestamp:
						c.cancelationForms[0]?.timestamp ?? c.rescheduleForms[0]?.timestamp,
					issueType: c.issueType,
					issueDescription: c.issueDescription,
					installDate: c.installDateTime,
					status: c.accountStatus,
					phone: c.cellPhone,
					lead: c.lead,
					crmId: c.crmId,
					image: c.user?.profileImage?.url,
					cancelationForms: c.cancelationForms || [],
					rescheduleForms: c.rescheduleForms || [],
				}));
				for (let i = 0; i < result.length; i++) {
					result[i].timestamp = new Date(result[i].timestamp).toLocaleString();
					result[i].installDate = new Date(
						result[i].installDate,
					).toLocaleString();
				}

				setTotalPages(res.data.totalPages);
				setData(result);
			} catch (error) {
				console.error(error);
			} finally {
				setLoading(false);
				setInit(false);
			}
		};

		fetchData();
	}, [page, debouncedSearch, accountStatus, issueType, fiberCompanyId]);

	const contactButtons = useMemo(() => new ContactButtons(), []);

	const renderForms = (forms, type, timestamp) => {
		console.log(type);
		return forms.map((form, index) => {
			const route =
				type === "Cancellation"
					? `/issue-cancel-details/${form.id}`
					: `/issue-reschedule-details/${form.id}`;
			return (
				<span
					key={form.id}
					onClick={() => navigate(route)}
					style={{
						color: form.isResolved ? "green" : "red",
						cursor: "pointer",
						marginBottom: "0.5rem",
						// width: "max-content",
						textWrap: "wrap",
					}}
				>
					{timestamp} {type} {index + 1}
				</span>
			);
		});
	};

	const openMap = (lead) => {
		if (lead.fiberHouse && lead.fiberHouse.coordinates) {
			navigate("/map", {
				state: {
					fiberHouse: {
						...lead.fiberHouse,
						leadId: lead.id,
						lead: {
							...lead,
							fiberHouse: null,
						},
					},
				},
			});
		} else {
			Swal.fire({
				icon: "error",
				title: "No Coordinates",
				text: "This lead does not have any coordinates",
			});
		}
	};

	if (init) return <AccountIssuesSkeleton />;

	return (
		<>
			<CustomerInfo
				customer={selectedCustomerForInfo}
				editable={true}
				open={!!selectedCustomerForInfo}
				setOpen={() => setSelectedCustomerForInfo(null)}
			/>

			<Card className="flex-1 pb-4">
				<CardHeader className="flex flex-col gap-4 xl:py-5 xl:px-6 xl:items-center xl:justify-between xl:flex-row">
					<CardTitle className="text-base font-medium">
						Manage Account Issues
					</CardTitle>
					<div className="flex flex-col gap-3 xl:gap-4 xl:flex-row">
						<div className="relative">
							<div className="absolute left-3 h-full flex items-center justify-center">
								<Search className="h-5 w-5 text-gray-500" />
							</div>
							<input
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
								type="search"
								placeholder="Search"
								className="w-full border border-zinc-300 rounded-lg focus:outline-none px-3 py-2 pl-10"
							/>
						</div>
						<div className="flex gap-2 justify-around" ref={dropdownRef}>
							<div className="relative flex-1">
								<button
									onClick={() => {
										setShowAccountStatusDropdown(!showAccountStatusDropdown);
										setShowIssueTypeDropdown(false);
									}}
									className="w-full text-nowrap px-3 py-2.5 flex gap-1 items-center justify-between border border-zinc-300 rounded-lg"
								>
									<div className="flex items-center gap-1">
										<Filter className="h-4 w-4" />
										<span>{accountStatus || "Account Status"}</span>
									</div>
									<ChevronDown className="h-4 w-4" />
								</button>
								{showAccountStatusDropdown && (
									<div className="absolute z-20 mt-1 w-full bg-white border border-zinc-300 rounded-lg shadow-lg">
										{accountStatuses.map((status, index) => (
											<div
												key={index}
												className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
												onClick={() => {
													setFiltersWrapper((prev) => ({
														...prev,
														accountStatus: status,
													}));
													setShowAccountStatusDropdown(false);
												}}
											>
												{status || "All"}
											</div>
										))}
									</div>
								)}
							</div>
							<div className="relative flex-1">
								<button
									onClick={() => {
										setShowIssueTypeDropdown(!showIssueTypeDropdown);
										setShowAccountStatusDropdown(false);
									}}
									className="w-full text-nowrap px-3 py-2.5 flex gap-1 items-center justify-between border border-zinc-300 rounded-lg"
								>
									<div className="flex items-center gap-1">
										<Filter className="h-4 w-4" />
										<span>{issueType || "Issue Type"}</span>
									</div>
									<ChevronDown className="h-4 w-4" />
								</button>
								{showIssueTypeDropdown && (
									<div className="absolute z-20 mt-1 w-full bg-white border border-zinc-300 rounded-lg shadow-lg">
										{issueTypes.map((type, index) => (
											<div
												key={index}
												className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
												onClick={() => {
													setFiltersWrapper((prev) => ({
														...prev,
														issueType: type,
													}));
													setShowIssueTypeDropdown(false);
												}}
											>
												{type || "All"}
											</div>
										))}
									</div>
								)}
							</div>
						</div>
					</div>
				</CardHeader>
				<CardContent className="py-0 xl:py-0 xl:border-t border-t-gray-200">
					<div className="hidden xl:table w-full">
						{loading ? (
							<>
								<AccountIssueRowSkeleton />
								<AccountIssueRowSkeleton />
								<AccountIssueRowSkeleton />
							</>
						) : (
							<>
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead className="text-gray-400">Name</TableHead>
											<TableHead className="text-gray-400">Timestamp</TableHead>
											<TableHead className="text-gray-400">
												Issue Type
											</TableHead>
											<TableHead className="text-gray-400">
												Install Date
											</TableHead>
											<TableHead className="text-gray-400">
												Issue Description
											</TableHead>
											<TableHead className="text-gray-400">Status</TableHead>
											<TableHead className="text-gray-400">Contact</TableHead>
											<TableHead className="text-gray-400">Go to</TableHead>
											<TableHead className="text-gray-400">Issues</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{data.map((issue) => (
											<TableRow
												key={issue.id}
												onClick={() => {
													console.log(issue);
												}}
											>
												<TableCell className="font-medium text-sm">
													<div className="flex items-center gap-3">
														<img
															src={issue.image ?? anonUser}
															className="w-10 h-10"
															alt={issue.issueType}
														/>
														{issue.name}
													</div>
												</TableCell>
												<TableCell className="text-slate-600 text-sm">
													{issue.timestamp}
												</TableCell>
												<TableCell className="text-slate-600 text-sm">
													{issue.issueType}
												</TableCell>
												<TableCell className="text-slate-600 text-sm">
													{issue.installDate}
												</TableCell>
												<TableCell className="text-slate-600 text-sm">
													{issue.issueDescription}
												</TableCell>
												<TableCell className="">
													<div
														className={clsx(
															getStatusColor(issue.status),
															"inline-block py-0.5 px-2 text-xs font-medium text-center rounded-full border",
														)}
													>
														{issue.status}
													</div>
												</TableCell>
												<TableCell className="">
													<div className="flex gap-2 items-center">
														<button
															onClick={() =>
																contactButtons.emailLead(issue.phone)
															}
														>
															<MdOutlineMail
																className="cursor-pointer"
																color="#81848A"
																size={20}
															/>
														</button>
														<button
															onClick={() =>
																contactButtons.whatsappLead(issue.lead)
															}
														>
															<FaWhatsapp
																className="cursor-pointer"
																color="#81848A"
																size={20}
															/>
														</button>
														<button
															onClick={() =>
																contactButtons.callLead(issue.lead)
															}
														>
															<MdOutlinePhone
																className="cursor-pointer"
																color="#81848A"
																size={20}
															/>
														</button>
													</div>
												</TableCell>
												<TableCell className="gap-2 items-center">
													<div className="flex gap-2 items-center">
														<button
															onClick={() => setSelectedCustomerForInfo(issue)}
														>
															<img
																src={
																	"/images/digital_tool_belt/customerInfo.svg"
																}
																alt="info"
																className="h-5 w-5 max-w-60"
															/>
														</button>
														<button onClick={() => openMap(issue.lead)}>
															<Map className="h-5 w-5" />
														</button>
														<button
															onClick={() => {
																let crmId =
																	issue?.crmId ?? issue.lead.offer.crmId;
																window.open(
																	`https://app.gohighlevel.com/v2/location/2bpuwFoxpdxZs2Oq2Z8a/contacts/detail/${crmId}`,
																);
															}}
														>
															<img
																src="/highlevelLogo.png"
																className="h-5 w-5 max-w-60"
																alt="HighLevel"
																color="#2761D0"
															/>
														</button>
													</div>
												</TableCell>
												<TableCell className="text-slate-600 text-sm">
													{/* cancelationForms: c.cancelationForms || [],
					rescheduleForms: c.rescheduleForms || [], */}
													<div className="flex flex-col">
														{renderForms(
															issue.issueType === "Reschedule"
																? issue.rescheduleForms
																: issue.cancelationForms,
															issue.issueType,
															issue.timestamp,
														)}
													</div>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
								<div className="flex justify-center items-center">
									<Pagination
										color="light"
										renderItem={(item) => (
											<PaginationItem
												components={{
													next: (props) => (
														<button {...props}>
															<BiRightArrow color="var(--color-icon)" />
														</button>
													),
													previous: (props) => (
														<button {...props}>
															<BiLeftArrow color="var(--color-icon)" />
														</button>
													),
												}}
												{...item}
												sx={{ color: "white" }}
											/>
										)}
										count={totalPages}
										page={page}
										onChange={(e, v) =>
											setFilters((prev) => ({ ...prev, page: v }))
										}
									/>
								</div>
							</>
						)}
					</div>

					<div className="xl:hidden flex flex-col gap-4">
						{loading ? (
							<>
								<AccountIssueMobileItemSkeleton />
								<AccountIssueMobileItemSkeleton />
							</>
						) : (
							<>
								{data.map((issue) => (
									<div
										key={issue.id}
										className="flex flex-col gap-3 rounded-lg border p-4"
									>
										<div className="flex items-center justify-between gap-3">
											<img
												src={issue.image ?? anonUser}
												className="w-10 h-10"
											/>
											<div className="flex flex-1">
												<div className="flex flex-col gap-0.5">
													<div className="font-medium text-sm">
														{issue.name}
													</div>
													<div className="text-slate-600 text-xs">
														{issue.timestamp}
													</div>
												</div>
											</div>
											<div className="flex gap-2 items-center">
												<div
													className={clsx(
														getStatusColor(issue.status),
														"py-0.5 px-2 text-xs font-medium flex text-center rounded-full border",
													)}
												>
													{issue.status}
												</div>
											</div>
										</div>
										<hr />
										<div className="flex justify-between">
											<div>
												<div className="font-medium text-xs text-gray-600">
													Issue Type
												</div>
												<div>{issue.issueType || "No Issue Type"}</div>
											</div>
											<div>
												<div className="font-medium text-right text-xs text-gray-600">
													Install Date
												</div>
												<div className="text-right">
													{issue.installDate || "No Install Date"}
												</div>
											</div>
										</div>

										<div className="flex justify-between">
											<div>
												<div className="font-medium text-xs text-gray-600">
													Issue Description
												</div>
												<div>{issue.issueDescription || "No Description"}</div>
											</div>
											<div>
												<div className="font-medium text-right text-xs text-gray-600">
													Issues
												</div>
												<div className="flex flex-col gap-0.5 text-right">
													{renderForms(
														issue.issueType === "Reschedule"
															? issue.rescheduleForms
															: issue.cancelationForms,
														issue.issueType,
														issue.timestamp,
													)}
												</div>
											</div>
										</div>

										<hr />

										<div className="flex gap-2">
											<button
												className="flex-1 px-3 py-2.5 flex gap-1 justify-center items-center border border-zinc-300 rounded-lg text-sm"
												onClick={() => setSelectedCustomerForInfo(issue)}
											>
												<img
													src={"/images/digital_tool_belt/customerInfo.svg"}
													alt="info"
													className="h-5 w-5"
												/>
												<div className="hidden md:block">Customer Info</div>
											</button>
											<button
												className="flex-1 px-3 py-2.5 flex gap-1 justify-center items-center border border-zinc-300 rounded-lg text-sm"
												onClick={() => openMap(issue.lead)}
											>
												<Map className="h-5 w-5" />
												<div className="hidden md:block">Map</div>
											</button>
											<button
												onClick={() => {
													let crmId = issue?.crmId ?? issue.lead.offer.crmId;
													window.open(
														`https://app.gohighlevel.com/v2/location/2bpuwFoxpdxZs2Oq2Z8a/contacts/detail/${crmId}`,
													);
												}}
												className="flex-1 px-3 py-2.5 flex gap-1 justify-center items-center border border-zinc-300 rounded-lg text-sm"
											>
												<img
													src="/highlevelLogo.png"
													className="h-5 w-5"
													alt="HighLevel"
													color="#2761D0"
												/>
											</button>
										</div>
										<div className="flex gap-2">
											<button
												className="flex-1 px-3 py-2.5 flex gap-1 justify-center items-center border border-zinc-300 rounded-lg text-sm"
												onClick={() => contactButtons.emailLead(issue.phone)}
											>
												<MdOutlineMail
													className="cursor-pointer"
													color="#81848A"
													size={20}
												/>
												<div className="hidden md:block">Mail</div>
											</button>
											<button
												className="flex-1 px-3 py-2.5 flex gap-1 justify-center items-center border border-zinc-300 rounded-lg text-sm"
												onClick={() => contactButtons.whatsappLead(issue.lead)}
											>
												<FaWhatsapp
													className="cursor-pointer"
													color="#81848A"
													size={20}
												/>
												<div className="hidden md:block">WhatsApp</div>
											</button>
											<button
												className="flex-1 px-3 py-2.5 flex gap-1 justify-center items-center border border-zinc-300 rounded-lg text-sm"
												onClick={() => contactButtons.callLead(issue.lead)}
											>
												<MdOutlinePhone
													className="cursor-pointer"
													color="#81848A"
													size={20}
												/>
												<div className="hidden md:block">Phone</div>
											</button>
										</div>
									</div>
								))}
								<div className="flex justify-center items-center">
									<Pagination
										color="light"
										renderItem={(item) => (
											<PaginationItem
												components={{
													next: (props) => (
														<button {...props}>
															<BiRightArrow color="var(--color-icon)" />
														</button>
													),
													previous: (props) => (
														<button {...props}>
															<BiLeftArrow color="var(--color-icon)" />
														</button>
													),
												}}
												{...item}
												sx={{ color: "white" }}
											/>
										)}
										count={totalPages}
										page={page}
										onChange={(e, v) =>
											setFilters((prev) => ({ ...prev, page: v }))
										}
									/>
								</div>
							</>
						)}
					</div>
				</CardContent>
			</Card>
		</>
	);
}
